import React, { useContext, useEffect, useRef, useState } from "react";

import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useParams, useSearchParams } from "../../../hooks/useNavigation";
import pluralize from "pluralize";
import { Col } from "react-bootstrap";
import styled from "styled-components";

import ComponentContext from "../../../../contexts/ComponentContext";
import { useStores } from "../../../../contexts/StoreContext";
import Loader from "../../Loader/Loader";
import ThumbnailSearchResultsPagination from "./ThumbanailSearchResultsPagination";
import Button from "../../Button/Button";

const StyledVacancyThumbnail = styled.div`
  height: 0;
  padding-top: 70%;
  position: relative;
`;

const StyledOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
`;

const StyledCardWrapper = styled.div`
  cursor: pointer;
`;

const StyledHeadingWrapper = styled.div`
  min-height: 3rem;
`;

const ITEMS_PER_PAGE = 12;

function ThumbnailSearchResults() {
  const { filtersStore, rolesStore } = useStores();
  const { searchResults, pagination, isFetching } = filtersStore;
  const {
    thumbnail,
    displayVancanciesCount = true,
    useComanyLogoAsThumbnail,
    itemsPerPage,
  } = useContext(ComponentContext);
  const searchResultsCount = searchResults.length;

  const itemsToDisplay = itemsPerPage || ITEMS_PER_PAGE;

  const [paginatedSearchResults, setPaginatedSearchResults] =
    useState(searchResults);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const searchParams = useSearchParams();

  useEffect(() => {
    const endOffset = currentPage + itemsToDisplay;

    setPaginatedSearchResults(searchResults.slice(currentPage, endOffset));
    setPageCount(Math.ceil(searchResultsCount / itemsToDisplay));
  }, [currentPage, itemsToDisplay, JSON.stringify(searchResults)]);

  const hasMounted = useRef(false);
  const searchParamsString = searchParams.toString();

  useEffect(() => {
    filtersStore.search().then(() => {
      hasMounted.current = true;
    });
    setCurrentPage(0);
  }, [searchParamsString]);

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsToDisplay) % pagination.totalResults;
    setCurrentPage(newOffset);
  };

  return (
    <div>
      <StyledHeadingWrapper className="mb-2 mb-md-5 d-flex justify-content-center align-items-center">
        {isFetching && <Loader minHeight="2rem" />}
        {!isFetching && (
          <h2 className="m-0 text-center text-uppercase">
            {pluralize("job", pagination.totalResults, displayVancanciesCount)}{" "}
            found
          </h2>
        )}
      </StyledHeadingWrapper>

      <div className="row gy-3 gy-lg-5 mb-5">
        {paginatedSearchResults.map((vacancy) => {
          const vacancyRole = rolesStore.roles.find(
            (role) => role.roleName === vacancy.category
          );

          const locationString = [
            vacancy?.company.name,
            vacancy?.location?.city,
            vacancy?.location?.county,
          ]
            .filter(Boolean)
            .join(", ");

          const href = `/job/${vacancy.id}`;

          let thumbnailUrl = vacancyRole?.thumbnail?.url || thumbnail?.url;

          if (useComanyLogoAsThumbnail && vacancy?.company?.logoUrlFull) {
            thumbnailUrl = vacancy?.company?.logoUrlFull;
          }

          return (
            <Col key={vacancy.id} md={4}>
              <Link href={href} passHref legacyBehavior>
                <StyledCardWrapper className="d-flex flex-column h-100">
                  <div className="bg-white">
                    <StyledVacancyThumbnail className="position-relative">
                      {thumbnailUrl && (
                        <Image
                          alt={vacancy.jobTitle}
                          className="position-absolute t-0"
                          layout="fill"
                          objectFit="cover"
                          src={thumbnailUrl}
                        />
                      )}
                      <StyledOverlay className="position-absolute top-0 w-100 bottom-0 d-flex align-items-center justify-content-center">
                        <h3 className="text-center mb-0 text-white fw-bold h2 text-uppercase">
                          {vacancy.jobTitle}
                        </h3>
                      </StyledOverlay>
                    </StyledVacancyThumbnail>
                  </div>
                  <div className="bg-white p-4 flex-grow-1 d-flex flex-column">
                    <div className="pb-5 fw-bold fs-5">
                      <p>{locationString}</p>
                      <p>{vacancy.remuneration?.description}</p>
                    </div>
                    <div className="d-flex justify-content-center mt-auto">
                      <Button className="w-50 mx-auto d-flex justify-content-center align-items-center">
                        READ MORE
                      </Button>
                    </div>
                  </div>
                </StyledCardWrapper>
              </Link>
            </Col>
          );
        })}
      </div>
      {pagination.totalResults > itemsToDisplay ? (
        <div className="pt-5">
          <ThumbnailSearchResultsPagination
            currentPage={currentPage}
            handlePageClick={handlePageClick}
            itemsPerPage={itemsToDisplay}
            pageCount={pageCount}
            searchResultsCount={pagination.totalResults}
          />
        </div>
      ) : null}
    </div>
  );
}

export default observer(ThumbnailSearchResults);
